import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, AlertCircle, CheckCircle, Image as ImageIcon } from 'lucide-react';
import { rawFetch } from '../../services/api';

interface ProfileImageUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  onUploadComplete: (profileUrl: string) => void;
}

interface UploadState {
  status: 'idle' | 'validating' | 'encoding' | 'uploading' | 'confirming' | 'complete' | 'error';
  progress: number;
  error: string | null;
}

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MIN_SIZE = 500; // min 500x500
const MAX_SIZE = 4000; // max 4000x4000
const WEBP_QUALITY = 0.82;

/**
 * Loads an image file and returns dimensions.
 */
function loadImage(file: File): Promise<{ img: HTMLImageElement; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => resolve({ img, width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
}

/**
 * Encode image to WebP via Canvas.
 */
function encodeToWebP(img: HTMLImageElement, width: number, height: number, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) { reject(new Error('Canvas context not available')); return; }
    ctx.drawImage(img, 0, 0, width, height);
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('Failed to encode image')),
      'image/webp',
      quality
    );
  });
}

export default function ProfileImageUploadModal({ isOpen, onClose, slug, onUploadComplete }: ProfileImageUploadModalProps) {
  const [uploadState, setUploadState] = useState<UploadState>({ status: 'idle', progress: 0, error: null });
  const [dragOver, setDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setUploadState({ status: 'idle', progress: 0, error: null });
      setPreviewUrl(null);
      setDragOver(false);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (uploadState.status === 'uploading' || uploadState.status === 'confirming' || uploadState.status === 'encoding') {
      return; // prevent closing during active upload
    }
    onClose();
  }, [uploadState.status, onClose]);

  const validateFile = useCallback((file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) return 'Invalid file type. Allowed: JPEG, PNG, WebP';
    if (file.size > MAX_FILE_SIZE) return 'File too large. Maximum size is 10MB';
    return null;
  }, []);

  const uploadFile = useCallback(async (file: File) => {
    setUploadState({ status: 'validating', progress: 0, error: null });

    const validationError = validateFile(file);
    if (validationError) {
      setUploadState({ status: 'error', progress: 0, error: validationError });
      return;
    }

    // Load and validate dimensions
    let imgData: { img: HTMLImageElement; width: number; height: number };
    try {
      imgData = await loadImage(file);
    } catch {
      setUploadState({ status: 'error', progress: 0, error: 'Failed to read image.' });
      return;
    }

    const { img, width, height } = imgData;

    if (width < MIN_SIZE || height < MIN_SIZE) {
      setUploadState({ status: 'error', progress: 0, error: `Too small. Image must be at least ${MIN_SIZE}×${MIN_SIZE}px. Yours is ${width}×${height}.` });
      URL.revokeObjectURL(img.src);
      return;
    }

    if (width > MAX_SIZE || height > MAX_SIZE) {
      setUploadState({ status: 'error', progress: 0, error: `Too large. Max ${MAX_SIZE}×${MAX_SIZE}px. Yours is ${width}×${height}.` });
      URL.revokeObjectURL(img.src);
      return;
    }

    // Check square aspect ratio (allow minor deviation)
    const ratio = width / height;
    if (ratio < 0.9 || ratio > 1.1) {
      setUploadState({ status: 'error', progress: 0, error: `Image should be square (1:1). Yours is ${width}×${height}.` });
      URL.revokeObjectURL(img.src);
      return;
    }

    setPreviewUrl(img.src);

    // Encode to WebP
    setUploadState({ status: 'encoding', progress: 5, error: null });

    let optimizedBlob: Blob;
    try {
      optimizedBlob = await encodeToWebP(img, width, height, WEBP_QUALITY);
      if (optimizedBlob.size >= file.size && file.type === 'image/webp') {
        optimizedBlob = file;
      }
    } catch {
      setUploadState({ status: 'error', progress: 0, error: 'Failed to optimize image.' });
      return;
    }

    const uploadContentType = optimizedBlob.type || 'image/webp';

    // Get presigned URL
    setUploadState({ status: 'uploading', progress: 10, error: null });

    let presignData: { uploadUrl: string; path: string };
    try {
      const res = await rawFetch('/api/public/tenants/profile/presign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug },
        body: JSON.stringify({ contentType: uploadContentType, fileSize: optimizedBlob.size }),
      });
      if (!res.ok) {
        const errData = await res.json() as { error: string };
        setUploadState({ status: 'error', progress: 0, error: errData.error || 'Failed to prepare upload' });
        return;
      }
      presignData = await res.json() as typeof presignData;
    } catch {
      setUploadState({ status: 'error', progress: 0, error: 'Network error. Please check your connection.' });
      return;
    }

    // Upload with XHR for progress
    try {
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const pct = Math.round(10 + (event.loaded / event.total) * 75);
            setUploadState((prev) => ({ ...prev, progress: Math.min(pct, 85) }));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed with status ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.open('PUT', presignData.uploadUrl);
        xhr.setRequestHeader('Content-Type', uploadContentType);
        xhr.send(optimizedBlob);
      });
    } catch {
      setUploadState({ status: 'error', progress: 0, error: 'Upload failed. Please try again.' });
      return;
    }

    // Confirm with backend
    setUploadState({ status: 'confirming', progress: 88, error: null });

    try {
      const confirmRes = await rawFetch('/api/public/tenants/profile/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug },
        body: JSON.stringify({ path: presignData.path, width, height }),
      });

      if (!confirmRes.ok) {
        const errData = await confirmRes.json() as { error: string };
        setUploadState({ status: 'error', progress: 0, error: errData.error || 'Failed to process image' });
        return;
      }

      const confirmData = await confirmRes.json() as { profileUrl: string };

      setUploadState({ status: 'complete', progress: 100, error: null });

      setTimeout(() => {
        onUploadComplete(confirmData.profileUrl);
        onClose();
      }, 1500);
    } catch {
      setUploadState({ status: 'error', progress: 0, error: 'Failed to confirm upload.' });
    }
  }, [slug, validateFile, onUploadComplete, onClose]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  }, [uploadFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); setDragOver(true); }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => { e.preventDefault(); setDragOver(false); }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  }, [uploadFile]);

  const isUploading = uploadState.status === 'uploading' || uploadState.status === 'confirming' || uploadState.status === 'validating' || uploadState.status === 'encoding';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="relative w-full max-w-md bg-[#1A1A1A] border border-white/10 rounded-xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
              <h2 className="text-lg font-semibold text-white">Upload Profile Image</h2>
              <button
                onClick={handleClose}
                disabled={isUploading}
                className="p-1.5 rounded-lg hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              >
                <X className="w-5 h-5 text-text-secondary" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Error */}
              <AnimatePresence>
                {uploadState.error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-4 overflow-hidden"
                  >
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-300">{uploadState.error}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Upload area or Progress */}
              {uploadState.status === 'idle' || uploadState.status === 'error' ? (
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => fileInputRef.current?.click()}
                  className={`
                    relative flex flex-col items-center justify-center p-10 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200
                    ${dragOver
                      ? 'border-electric-cyan bg-electric-cyan/5 scale-[1.01]'
                      : 'border-white/20 hover:border-white/40 hover:bg-white/[0.02]'
                    }
                  `}
                >
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 transition-colors ${
                    dragOver ? 'bg-electric-cyan/20' : 'bg-white/5'
                  }`}>
                    <Upload className={`w-6 h-6 ${dragOver ? 'text-electric-cyan' : 'text-text-secondary'}`} />
                  </div>

                  <p className="text-sm font-medium text-white mb-1">
                    {dragOver ? 'Drop your image here' : 'Drag & drop your profile image'}
                  </p>
                  <p className="text-xs text-text-muted">or click to select a file</p>

                  <div className="mt-4 flex items-center gap-4 text-xs text-text-muted">
                    <span className="flex items-center gap-1">
                      <ImageIcon className="w-3.5 h-3.5" />
                      JPG, PNG, WebP
                    </span>
                    <span>•</span>
                    <span>Square, {MIN_SIZE}×{MIN_SIZE} min</span>
                    <span>•</span>
                    <span>Max 10MB</span>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Preview */}
                  {previewUrl && (
                    <div className="mx-auto w-48 h-48 rounded-xl overflow-hidden border border-white/10">
                      <img src={previewUrl} alt="Profile preview" className="w-full h-full object-cover" />
                    </div>
                  )}

                  {/* Progress bar */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-text-secondary">
                        {uploadState.status === 'validating' && 'Validating...'}
                        {uploadState.status === 'encoding' && 'Optimizing image...'}
                        {uploadState.status === 'uploading' && 'Uploading...'}
                        {uploadState.status === 'confirming' && 'Processing...'}
                        {uploadState.status === 'complete' && 'Upload complete!'}
                      </span>
                      <span className="text-sm font-mono text-electric-cyan">
                        {uploadState.progress}%
                      </span>
                    </div>

                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                      <motion.div
                        className={`h-full rounded-full ${
                          uploadState.status === 'complete' ? 'bg-green-500' : 'bg-electric-cyan'
                        }`}
                        initial={{ width: 0 }}
                        animate={{ width: `${uploadState.progress}%` }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}
                      />
                    </div>

                    {uploadState.status === 'complete' && (
                      <motion.div
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-2 text-sm text-green-400"
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span>Profile image updated</span>
                      </motion.div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
